import React from 'react';

import {
	BLChannelProfile,
	BrandliveEvent,
	EProfileSectionTypes,
	ProfileSection,
	RegistrationQuestion,
	RegistrationStep,
	SocialLink,
	SocialPlatforms,
	BlProfileValues,
	RegistrationStepType,
	Question,
	PERSONAL_INFO_FIELDS,
	QuestionType,
	RegFieldsEnum,
} from '../../../types/working-model';
import { isDevEnvironment } from '../../../utils/utils';
import { GetAttendeeProfile } from '../../../connection/social';

export interface MungedSection extends Omit<ProfileSection, 'fields'> {
	fields: RegistrationQuestion[];
}
interface MungedProfileSections {
	generalSection: MungedSection;
	otherSections: MungedSection[];
}

export function getMungedProfileSections(eventBundle: BrandliveEvent | null, personal?: boolean, allRegistrationQuestions?: RegistrationQuestion[]): MungedProfileSections {
	const sections: ProfileSection[] = [...eventBundle?.social_settings?.profiles.sections || []];
	// if we are in admin we get the registration questions passed in because the event bundle questions won't be updated until published
	const registrationQuestions: RegistrationQuestion[] = allRegistrationQuestions || eventBundle?.registration_questions || [];
	const generalInfoStep: RegistrationStep | undefined = eventBundle?.registration_steps?.find(step => step.type === RegistrationStepType.general);
	const profileStep: RegistrationStep | undefined = eventBundle?.registration_steps?.find(step => step.type === RegistrationStepType.profile);
	// PersonalInfoFilter to prevent displaying personal fields to other users, if this is a personal profile the filter is empty
	const personalInfoFilter = personal ? [] : PERSONAL_INFO_FIELDS;

	// general section has to be separated out because it is typically formatted differently on profile pages
	const generalSectionIndex = sections.findIndex(section => section.type === EProfileSectionTypes.GeneralInformation);
	const generalSectionUnmodified: ProfileSection = sections.splice(generalSectionIndex, 1)[0];
	// general section does not contain it's own independent fields - it must be completed from the General Info step of registration
	const generalSectionFields: RegistrationQuestion[] = generalInfoStep?.questions?.filter(question => !personalInfoFilter.includes(question.registration_question)) || [];
	const generalSection: MungedSection = { ...generalSectionUnmodified, fields: generalSectionFields };

	// for all other sections... only return sections that are turned on and have fields
	const otherSections: MungedSection[] = sections.filter(section => section.isOn && section.fields.length).map(section => {
		const fieldsUpdated: RegistrationQuestion[] = [];

		// special handling of About section dependent on Profile registration step
		if (section.type === EProfileSectionTypes.About && profileStep?.isOn) {
			const filteredProfileFields = profileStep.questions?.filter(question => !personalInfoFilter.includes(question.registration_question)) || [];
			fieldsUpdated.push(...filteredProfileFields);
		} else {
			section.fields.forEach((field: Question) => {
				const findQuestion: RegistrationQuestion | undefined = registrationQuestions.find(question => question.registration_question === field.questionKey);
				if (findQuestion && !personalInfoFilter.includes(findQuestion.registration_question)) {
					fieldsUpdated.push(findQuestion);
				}
			});
		}

		return { ...section, fields: fieldsUpdated };
	});

	return { generalSection, otherSections };
}

export const showField = (question: RegistrationQuestion, profile: BLChannelProfile): boolean => {
	const id = String(question.registration_question);

	if (question.registration_question === RegFieldsEnum.passcode) {
		return false;
	}

	if (question.type === QuestionType.checkbox && profile?.[id] !== undefined) {
		return true;
	}

	if (profile?.[id] && profile[id]?.length) {
		return true;
	}

	return false;
};

export function getSocialsFromProfile(profile: BLChannelProfile): SocialLink[] {
	const socials: SocialLink[] = [];

	for (const platform of Object.values(SocialPlatforms)) {
		// if the social link exists and it has content
		if (profile?.[platform]) {
			const formatted = {
				url: profile[platform],
				platform
			};
			socials.push(formatted);
		}
	}

	return socials;
}

export const placeHolderAttendee = {
	[BlProfileValues.firstName]: '',
	[BlProfileValues.lastName]: '',
};

export function getAttendeeProfile(
	attendeeId: string,
	eventUuid: string,
	blProfileUserToken: string,
	setAttendee: React.Dispatch<React.SetStateAction<BLChannelProfile>>,
	onError: () => void,
	setLoading: React.Dispatch<React.SetStateAction<boolean>>
): void {
	if (attendeeId && eventUuid && blProfileUserToken) {
		if (!isDevEnvironment()) {
			const eventNamePass = window.location.pathname.split("/")?.[1];
			if (eventNamePass) {
				const host = window.location.hostname;
				setLoading(true);
				GetAttendeeProfile(attendeeId, blProfileUserToken, host.replace(/\.brandlive.*\.com/, '.brand.live'), eventNamePass)
					.then(res => setAttendee(res))
					.catch(e => {
						console.error(e);
						onError();
					})
					.finally(() => setLoading(false));
			}
		} else {
			setLoading(true);
			GetAttendeeProfile(attendeeId, blProfileUserToken, 'dev', eventUuid)
				.then(res => setAttendee(res))
				.catch(e => {
					console.error(e);
					onError();
				})
				.finally(() => setLoading(false));
		}
	}
}

// Re-export React components from .tsx so bare-path imports of this module resolve correctly
export { ProfileFields, CardTitle, LeaderboardPointTotal, LeaderboardAction } from './profile-utils.tsx';

export const getDirectoryFilters = (event: BrandliveEvent | null): RegistrationQuestion[] => {
	if (!event) return [];

	const filtersMap = event?.social_settings?.directory?.filters?.reduce((accum: { [key: number]: number }, curr) => {
		if (!curr.registrationQuestionId) return accum;
		return { ...accum, [curr.registrationQuestionId]: curr.registrationQuestionId };
	}, {});

	const registrationFilterQuestions = event?.registration_steps?.reduce((accum: RegistrationQuestion[], curr: RegistrationStep): RegistrationQuestion[] => {
		curr?.questions?.forEach(qn => {
			if (!filtersMap?.[qn.registration_question]) return accum;
			accum.push(qn);
		});
		return accum;
	}, []) ?? [];

	return registrationFilterQuestions;
};
